import { Stack, Typography } from '@mui/material';
import React, { cloneElement, isValidElement, useState } from 'react';

import { CloseIcon } from '@/shared/assets/icons/close-icon';
import { IconButton } from '@/shared/components/ui/button/icon-button/icon-button';
import {
  ContentStack,
  CustomDivider,
  Drawer,
  PanelHeader,
  TopContent,
} from '@/shared/components/ui/drawer-controller/drawer-controller.styled';
import {
  TControllerTriggerProps,
  TDrawerControllerProps,
} from '@/shared/components/ui/drawer-controller/drawer-controller.types';
import { useHasPermission } from '@/shared/features/permissions/hooks/use-permission-check/use-permission-check';

export const DrawerController: React.FC<TDrawerControllerProps> = ({
  trigger,
  title,
  headerActions,
  description,
  children,
  anchor = 'right',
  width = '500px',
  initialOpen = false,
  showDivider = true,
  onClose,
  onBeforeClose,
  onTriggerClick,
  permission,
  disabled,
}) => {
  const [open, setOpen] = useState<boolean>(initialOpen);
  const hasPermission = useHasPermission(permission);

  const handleClose = () => {
    if (onBeforeClose && !onBeforeClose()) {
      return;
    }

    setOpen(false);
    onClose?.();
  };

  const handleOpen = (event: React.BaseSyntheticEvent) => {
    event.stopPropagation();

    if (disabled) {
      return;
    }

    setOpen(true);
    onTriggerClick?.(event, handleClose);
  };

  const renderTrigger = () => {
    const triggerElement =
      typeof trigger === 'function' ? trigger(open) : trigger;

    if (!isValidElement<TControllerTriggerProps>(triggerElement)) {
      console.error('Trigger must be a valid React element');
      return null;
    }

    if (disabled) {
      return cloneElement(triggerElement, { onClick: undefined });
    }

    return cloneElement(triggerElement, {
      onClick: (event: React.BaseSyntheticEvent) => {
        event.preventDefault();
        handleOpen(event);
        triggerElement.props.onClick?.(event);
      },
    });
  };

  const renderTitle = () => {
    if (typeof title === 'function') {
      return title(handleClose);
    }
    if (typeof title === 'string') {
      return <Typography variant="h5">{title}</Typography>;
    }
    return title;
  };

  if (!hasPermission) {
    return null;
  }

  return (
    <>
      {renderTrigger()}
      <Drawer
        onClick={(event) => event.stopPropagation()}
        anchor={anchor}
        open={open}
        $width={width}
        onClose={handleClose}
      >
        <Stack height="100%" minHeight={0}>
          <TopContent>
            <PanelHeader>
              {renderTitle()}
              <Stack direction="row" alignItems="center" gap="0.5rem">
                {headerActions}
                <IconButton
                  onClick={handleClose}
                  data-testid="drawer-close-button"
                  aria-label="Close drawer"
                >
                  <CloseIcon />
                </IconButton>
              </Stack>
            </PanelHeader>
            {description && (
              <Typography variant="p8" color="textSecondary">
                {description}
              </Typography>
            )}
            {showDivider && <CustomDivider />}
          </TopContent>
          <ContentStack height="100%" minHeight={0} width="100%">
            {children(handleClose, open)}
          </ContentStack>
        </Stack>
      </Drawer>
    </>
  );
};
